import { useCallback, useEffect } from "react";
import { Button } from "../PSY/Button";
import { LiquityStoreState, Decimal, Trove, Decimalish, SLSD_MINIMUM_DEBT, Percent, Difference, UserTrove } from "@liquity/lib-base";

import { LiquityStoreUpdate, useLiquityReducer, useLiquitySelector } from "@liquity/lib-react";
import { useMyTransactionState } from "../Transaction";

import { TroveAction } from "./TroveAction";
import { useTroveView } from "./context/TroveViewContext";

import {
  selectForTroveChangeValidation,
  validateTroveChange
} from "./validation/validateTroveChange";

import { LoadingOverlay } from "../LoadingOverlay";
import { CollateralLexicons, SLSD } from "src/lexicon";
import { useAppState } from "../../hooks/AppStateContext";

// type TroveManagerState = ReturnType<typeof init>;
type TroveManagerState = {
  original: UserTrove
  edited: Trove
  changePending: boolean
  debtDirty: boolean
  addedMinimumDebt: boolean
  collateralKey: string
}
type TroveManagerAction =
  | LiquityStoreUpdate
  | { type: "startChange" | "finishChange" | "revert" | "addMinimumDebt" | "removeMinimumDebt" }
  | { type: "setCollateral" | "setDebt"; newValue: Decimalish };

const reduceWith = (action: TroveManagerAction) => (state: TroveManagerState): TroveManagerState =>
  reduce(state, action);

const addMinimumDebt = reduceWith({ type: "addMinimumDebt" });
const removeMinimumDebt = reduceWith({ type: "removeMinimumDebt" });
const finishChange = reduceWith({ type: "finishChange" });
const revert = reduceWith({ type: "revert" });

const reduce = (state: TroveManagerState, action: TroveManagerAction): TroveManagerState => {
  // console.log(state);
  // console.log(action);

  const { original, edited, changePending, debtDirty, addedMinimumDebt, collateralKey } = state;

  switch (action.type) {
    case "startChange": {
      console.log("starting change");
      return { ...state, changePending: true };
    }

    case "finishChange":
      return { ...state, changePending: false };

    case "setCollateral": {
      const newCollateral = Decimal.from(action.newValue);

      const newState = {
        ...state,
        edited: edited.setCollateral(newCollateral)
      };

      if (!debtDirty) {
        if (edited.isEmpty && newCollateral.nonZero) {
          return addMinimumDebt(newState);
        }
        if (addedMinimumDebt && newCollateral.isZero) {
          return removeMinimumDebt(newState);
        }
      }

      return newState;
    }

    case "setDebt":
      return {
        ...state,
        edited: edited.setDebt(action.newValue),
        debtDirty: true
      };

    case "addMinimumDebt":
      return {
        ...state,
        edited: edited.setDebt(SLSD_MINIMUM_DEBT),
        addedMinimumDebt: true
      };

    case "removeMinimumDebt":
      return {
        ...state,
        edited: edited.setDebt(0),
        addedMinimumDebt: false
      };

    case "revert":
      return {
        ...state,
        edited: new Trove(original.collateral, original.debt),
        debtDirty: false,
        addedMinimumDebt: false
      };

    case "updateStore": {
      const {
        newState: { collateralDerivedStates },
        stateChange: { collateralStates }
        // stateChange: { troveBeforeRedistribution: changeCommitted }
      } = action;

      if (!collateralStates || !collateralKey) {
        return state
      }
      const { trove } = collateralDerivedStates[collateralKey];
      const { troveBeforeRedistribution: changeCommitted } = collateralStates[collateralKey];
      const newState = {
        ...state,
        original: trove
      };

      if (changePending && changeCommitted) {
        return finishChange(revert(newState));
      }

      const change = original.whatChanged(edited, 0);

      if (
        (change?.type === "creation" && !trove.isEmpty) ||
        (change?.type === "closure" && trove.isEmpty)
      ) {
        return revert(newState);
      }

      return { ...newState, edited: trove.apply(change, 0) };
    }
  }
};

const feeFrom = (original: Trove, edited: Trove, borrowingRate: Decimal): Decimal => {
  const change = original.whatChanged(edited, borrowingRate);

  if (change && change.type !== "invalidCreation" && change.params.borrowSLSD) {
    return change.params.borrowSLSD.mul(borrowingRate);
  } else {
    return Decimal.ZERO;
  }
};

const transactionIdPrefix = "trove-";
const transactionIdMatcher = new RegExp(`^${transactionIdPrefix}`);

type ClosingProps = {
  collateral?: Decimalish;
  debt?: Decimalish;
  collateralKey: string;
};

// 元々、Opening, AdjusitingとClosingと共用のコンポーネントだったが、Closingだけで使っている
export const Closing: React.FC<ClosingProps> = ({ collateral, debt, collateralKey }) => {
  const { currentAssetAddress } = useAppState()

  const init = useCallback(({ collateralDerivedStates }: LiquityStoreState) => {
    const { trove } = collateralDerivedStates[collateralKey];
    return {
      original: trove,
      edited: new Trove(trove.collateral, trove.debt),
      changePending: false,
      debtDirty: false,
      addedMinimumDebt: false,
      collateralKey
    }
  }, [collateralKey])

  const select = useCallback((state: LiquityStoreState) => {
    const { collateralStates, collateralDerivedStates } = state;
    const { price } = collateralStates[collateralKey];
    const { fees } = collateralDerivedStates[collateralKey];
    return {
      fees: fees,
      validationContext: selectForTroveChangeValidation(state, collateralKey),
      price: price,
    }
  }, [collateralKey]) ;

  const [{ original, edited, changePending }, dispatch] = useLiquityReducer(reduce, init);
  const selectedState = useLiquitySelector(select);

  useEffect(() => {
    if (collateral !== undefined) {
      dispatch({ type: "setCollateral", newValue: collateral });
    }
    if (debt !== undefined) {
      dispatch({ type: "setDebt", newValue: debt });
    }
  }, [collateral, debt, dispatch]);

  const { dispatchEvent } = useTroveView();

  const myTransactionState = useMyTransactionState(transactionIdMatcher);

  useEffect(() => {
    if (
      myTransactionState.type === "waitingForApproval" ||
      myTransactionState.type === "waitingForConfirmation"
    ) {
      dispatch({ type: "startChange" });
    } else if (myTransactionState.type === "failed" || myTransactionState.type === "cancelled") {
      dispatch({ type: "finishChange" });
    } else if (myTransactionState.type === "confirmedOneShot") {
      if (myTransactionState.id === `${transactionIdPrefix}closure`) {
        dispatchEvent("TROVE_CLOSED");
      } else {
        dispatchEvent("TROVE_ADJUSTED");
      }
    }
  }, [myTransactionState, dispatch, dispatchEvent]);

  if (selectedState === undefined) {
    return <></>
  }

  const { fees, validationContext } = selectedState;
  const borrowingRate = fees.borrowingRate();
  const maxBorrowingRate = borrowingRate.add(0.005); // WONT-FIX slippage tolerance

  const lexicon = CollateralLexicons.find(lexicon => lexicon.key === collateralKey)
  const [validChange, description] = validateTroveChange(
    original,
    edited,
    borrowingRate,
    validationContext,
    lexicon
  );

  return (
    <div className="px-4">

      {validChange && (
        <>
          <div>
            <div className="text-style-title">Close Position</div>
            <div className="flex flex-col mt-12 mb-4">
              <div className="flex flex-row items-baseline justify-between">
                <div className="text-style-sub">Repay</div>
                <div className="text-style-value ml-4">
                  {validChange.params.repaySLSD?.prettify()}
                  <span className="ml-0.5 text-style-attr">{SLSD.term}</span>
                </div>
              </div>
              <div className="flex flex-row items-baseline justify-between mt-2">
                <div className="text-style-sub">Withdraw</div>
                <div className="text-style-value">
                  {validChange.params.withdrawCollateral?.prettify()}
                  <span className="ml-0.5 text-style-attr">{lexicon?.term}</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="mt-4">
        {description}
      </div>
      <div className="flex justify-end">
        {validChange && !!currentAssetAddress ? (
          <TroveAction
            transactionId={`${transactionIdPrefix}${validChange.type}`}
            change={validChange}
            maxBorrowingRate={maxBorrowingRate}
            borrowingFeeDecayToleranceMinutes={60}
            disabled={!validChange}
            collateralAddress={currentAssetAddress}
          >
            Confirm
          </TroveAction>
        ) : (
          <Button disabled>Confirm</Button>
        )}
      </div>

      {changePending && <LoadingOverlay />}
    </div>
  );
};
